<template>
  <div class="relative">
    <div class="mb-6 flex flex-col-reverse md:flex-row items-center gap-x-6">
      <div class="rounded-3xl bg-white dark:bg-neutral-800/50 p-4">
        <div class="flex flex-col items-center gap-6 md:flex-row">
          <div class="w-full max-w-[200px] lg:max-w-[140px]">
            <img src="@/assets/perfilAjuda.svg" alt="" class="scale-125" />
          </div>
          <div class="w-full px-4 text-justify" v-html="$t('helpers.profile')"></div>
        </div>
      </div>

      <!-- Avatar -->
      <div class="relative">
        <AvatarUpload
          ref="avatarUploadRef"
          v-model="form.foto"
          shape="circle"
          :width="200"
          :height="200"
          :onConfirm="handleFileUpload"
        />
        <BirthdayTheme v-if="birthday" />
      </div>
    </div>

    <div>
      <ul class="mb-6 flex flex-wrap gap-x-6 cursor-pointer m-0 p-0 z-auto">
        <li
          v-for="item in tabs"
          :key="item.id"
          @click="activeTab(item)"
          class="navlink font-bold py-1"
          ref="selected"
          tabindex="0"
          :class="{ indicator: tabsSelected == item.id }"
          :data-selected="`${item.id}`"
        >
          {{ item.title }}
          <UiIcon v-tooltip="{ placement: 'top' }" icon="info-circle" size="1em" :title="item.info" />
        </li>
      </ul>

      <UiForm ref="formRef" :form-data="form" :rules="rules" auto-validate class="mb-6">
        <div v-if="tabsSelected == tabs[0].id" class="grid grid-cols-8 gap-x-4 gap-y-4">
          <UiFormControl
            :label="$t('labels.fullname')"
            prop="nomeCompleto"
            required
            class="col-span-full md:col-span-4"
          >
            <UiInput
              v-model.trim="form.nomeCompleto"
              id="title"
              maxlength="60"
              :placeholder="$t('placeholder.fullname')"
              class="w-full"
            />
          </UiFormControl>

          <UiFormControl
            class="col-span-full md:col-span-4"
            prop="apelido"
            :label="$t('labels.calledBy')"
            :tooltip="$t('tooltips.profile.calledBy')"
          >
            <UiInput
              v-model="form.apelido"
              id="apelido"
              class="w-full"
              :placeholder="$t('placeholder.enterNickname')"
            />
          </UiFormControl>

          <UiFormControl
            :label="$t('input.email')"
            prop="email"
            class="col-span-full md:col-span-4"
          >
            <UiInput
              v-model.trim="form.email"
              id="email"
              disabled
              :placeholder="$t('placeholder.email')"
              class="w-full"
            />
          </UiFormControl>

          <UiFormControl
            :label="$t('input.document')"
            class="col-span-full md:col-span-4"
          >
            <UiInput
              v-model="form.cpf"
              disabled
              :placeholder="$t('placeholder.enterDocument')"
              :mask="form.estrangeira ? '' : '###.###.###-##'"
              class="w-full"
            />
          </UiFormControl>

          <UiFormControl
            :label="$t('input.phone')"
            class="col-span-full md:col-span-4 lg:col-span-2"
          >
            <UiInputPhone class="w-full" v-model="form.telefone" />
          </UiFormControl>

          <UiFormControl
            :label="$t('input.birthDate')"
            prop="dataDeNascimento"
            class="relative col-span-full md:col-span-4 lg:col-span-2"
          >
            <UiDatePicker
              v-model="form.dataDeNascimento"
              id="dataDeNascimento"
              max-date="today"
              class="w-full"
            />
            <Transition
              appear
              enter-active-class="transition duration-500 ease-in-out"
              enter-from-class="opacity-0 scale-50 -translate-y-20"
            >
              <div v-if="birthday" id="birthdateInput" class="absolute bottom-1 right-8 text-3xl">🎂</div>
            </Transition>
          </UiFormControl>

          <UiFormControl
            :label="$t('input.gender')"
            class="col-span-full md:col-span-2"
          >
            <UiSelect
              :placeholder="$t('placeholder.select', { field: $t('input.gender') })"
              class="w-full"
              v-model="form.genero"
              :data="generosList"
            />
          </UiFormControl>

          <UiFormControl :label="$t('labels.preferLanguage')" class="col-span-full md:col-span-2">
            <UiSelect
              :placeholder="$t('placeholder.select', { field: $t('labels.preferLanguage') })"
              class="w-full"
              v-model="form.idiomaPreferido"
              :data="idiomasList"
            />
          </UiFormControl>

          <UiFormControl
            prop="usuariosCompetencias"
            :label="$t('labels.skill', 2)"
            class="col-span-full md:col-span-4"
            :tooltip="`
              <strong>${$t('tooltips.profile.skillsTitle', { limit: 10 })}</strong><br />
              ${$t('tooltips.profile.skillsDescription')}
            `"
          >
            <UiSelect
              v-model="form.usuariosCompetencias"
              searchable
              :data="competenciasStore.list.filter(el => el.active).map(el => ({ value: el.id, label: el.nome }))"
              :placeholder="$t('placeholder.select', { field: $t('labels.skill', 2) })"
              multiple
              :limit="10"
            />
          </UiFormControl>
        </div>

        <div v-if="tabsSelected == tabs[1].id" class="grid grid-cols-9 gap-8">
          <UiFormControl v-if="form.unidadeDeNegocioId" :label="$t('labels.unit')" class="col-span-3">
            <UiInput
              v-model="form.unidadeDeNegocioNome"
              type="text"
              placeholder=""
              class="w-full"
              disabled
            />
          </UiFormControl>

          <UiFormControl v-if="form.departamentoId" :label="$t('labels.department')" class="col-span-3">
            <UiInput
              v-model="form.departamentoNome"
              type="text"
              placeholder=""
              class="w-full"
              disabled
            />
          </UiFormControl>

          <UiFormControl v-if="form.cargoId" :label="$t('labels.jobTitle')" class="col-span-3">
            <UiInput
              v-model="form.cargoNome"
              type="text"
              placeholder=""
              class="w-full"
              disabled
            />
          </UiFormControl>

          <UiFormControl
            v-if="form.superiorDireto"
            :label="$t('labels.supervisor')"
            class="col-span-3"
          >
            <UiInput
              v-model="form.superiorDiretoNome"
              type="text"
              placeholder=""
              class="w-full"
              disabled
            />
          </UiFormControl>
          <UiFormControl v-if="form.dataDeAdmissao" :label="$t('input.admissionDate')" class="col-span-3">
            <UiDatePicker
              v-model="form.dataDeAdmissao"
              class="w-full"
              disabled
            />
          </UiFormControl>
        </div>

        <div v-if="tabsSelected == tabs[2].id" class="grid grid-cols-8 grid-rows-2 gap-8">
          <UiFormControl :label="$t('labels.educationLevel')" class="col-span-3">
            <UiSelect
              v-model="form.escolaridade"
              searchable
              :data="escolaridadeList"
              :placeholder="$t('placeholder.select', {field: $t('labels.educationLevel')})"
              filterable
            />
          </UiFormControl>
        </div>
      </UiForm>
    </div>
    <div id="profile" class="absolute mx-auto left-0 right-0 w-4"></div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted, nextTick, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReward } from 'vue-rewards'
import { arrToLabelValue, capitalizedName, getInitials, dialog, toast, validateFileSize, normalizePhone } from '@/common/util'
import {
  useAuthStore,
  useCompetenciasStore,
  useUsuariosStore,
} from '@/stores'

import UiForm from '@/components/ui/form.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiInput from '@/components/ui/input.vue'
import UiSelect from '@/components/ui/select.vue'
import UiFormControl from '@/components/ui/formControl.vue'
import UiDatePicker from '@/components/ui/datepicker.vue'
import UiInputPhone from '@/components/ui/inputNumberPhone.vue'

import AvatarUpload from '@/components/ui/avatarUpload.vue'
import BirthdayTheme from './birthdayTheme.vue'

import type { UsuarioType } from '@/types/types'
import { usersService } from '@/services/usuarios.service'

const avatarUploadRef = ref<InstanceType<typeof AvatarUpload>>()

const competenciasStore = useCompetenciasStore()
const usuariosStore = useUsuariosStore()
const authStore = useAuthStore()

const loaded = ref(false)
const busy = ref(false)
const tabsSelected = ref(1)
const selected = ref(null)
const formRef = ref()
const birthday = ref(false)
const originalCompetences = ref([])

const { t } = useI18n()

defineProps(['editId'])
const emit = defineEmits(['onComplete'])

const tabs = [
  {
    id: 1,
    title: t('tabs.personalData'),
    info: t('tooltips.profile.personalData')
  },
  {
    id: 2,
    title: t('tabs.corporateData'),
    info: t('tooltips.profile.corporateData')
  },
  {
    id: 3,
    title: t('tabs.academicData'),
    info: t('tooltips.profile.academicData')
  }
]

const defaultValues: UsuarioType = {
  nomeCompleto: null,
  email: null,
  senha: null,
  cpf: null,
  telefone: null,
  tipoPessoa: null,
  apelido: null,
  genero: null,
  idiomaPreferido: null,
  dataDeAdmissao: null,
  superiorDireto: null,
  interesseDoSuperior: null,
  interessesDoUsuario: [],
  notificarViaPush: false,
  notificarViaSMS: false,
  foto: null,
  storageUrlDaFoto: null,
  fotoLink: null,
  peopleId: null,
  cargoId: null,
  departamentoId: null,
  unidadeDeNegocioId: null,
  ativo: true,
  escolaridade: null,
  dataDeNascimento: null,
  usuariosCompetencias: [],
  cargoNome: null,
  unidadeDeNegocioNome: null,
  departamentoNome: null,
  superiorDiretoNome: null
}

const form = reactive({ ...defaultValues })

const rules = reactive({
  nomeCompleto: [
    {
      name: 'required',
      test: (value) => !!value.trim(),
      message: 'O nome é obrigatório'
    },
    {
      name: 'maxLength',
      test: (value) => `${value}`.length <= 60,
      message: 'O nome não pode ultrapassar 60 caracteres'
    }
  ],
  usuariosCompetencias: {
    name: 'maxSelected',
    test: (value) => value.length <= 10,
    message: t('validation.maxSelected', { max: 10 })
  }
})

const generosList = computed(() => ([
  { label: t('labels.male'), value: 'Masculino' },
  { label: t('labels.female'), value: 'Feminino' },
  { label: t('labels.nonBinary'), value: 'Não-Binário' }
]))
const idiomasList = computed(() => ([
  { label: t('labels.pt'), value: 'Português' },
  { label: t('labels.en'), value: 'Inglês' },
  { label: t('labels.es'), value: 'Espanhol' }
]))
const escolaridadeList = arrToLabelValue([
  'Ensino Fundamental Incompleto',
  'Ensino Fundamental Completo',
  'Ensino Médio Incompleto',
  'Ensino Médio Completo',
  'Formação Superior Incompleta',
  'Formação Superior Completa',
  'Pós-graduação Incompleta',
  'Pós-graduação Completa',
  'Mestrado Incompleto',
  'Mestrado Completo',
  'Doutorado Incompleto',
  'Doutorado Completo'
])

const activeTab = (item) => {
  tabsSelected.value = item.id
}

const resetPassword = async () => {
  dialog.open({
    title: 'Mudança de Senha',
    alt: true,
    message: `
      Será enviado um e-mail para : <strong>${form.email}</strong> com orientações para você redefinir a sua senha.
      <p>Após confirmar, acesse sua caixa de entrada, localize o e-mail e siga as instruções para alteração.</p>
      <p>Dica! Se o e-mail não estiver correto, contate agora mesmo o seu gestor ou administrador da plataforma.</p>`,
    complete: async () => {
      try {
        await usuariosStore.sendEmailChangePassword(form.email)
        toast.success({
          title: t('feedback.success'),
          message: t('feedback.successRequestPassword')
        })
      } catch (error) {
        toast.error({ message: error })
      }
    }
  })
}


/**
 * Ação de submeter formulário
 * @param opt Se informado "cancel" o fluxo de envio é interrompido
 * Caso contrário e caso o form seja válido um post/put é submetido
 * a API e em caso de sucesso o componente PAI recebe um emit "onComplete"
 * contendo a resposta da api
 */
const submit: any = async (opt?: String) => {
  if (opt === 'cancel') {
    emit('onComplete')
    return true
  }

  if (!formRef.value.validate()) {
    toast.error({
      message: t('feedback.formError')
    })

    return false
  }

  if (form.dataDeNascimento && new Date(form.dataDeNascimento) > new Date()) {
    toast.error({
      message: t('feedback.futureBirthday')
    })

    return false
  }

  let usuariosCompetencias = []

  if (form.usuariosCompetencias.length > 0) {
    usuariosCompetencias = form.usuariosCompetencias.map(competenciaId => {
      const existente = originalCompetences.value.find(item => item.competenciaId === competenciaId)

      return existente ? existente : { competenciaId }
    })
  }

  const formData = {
    ...form,
    nomeCompleto: capitalizedName(form.nomeCompleto),
    dataDeNascimento: form.dataDeNascimento || null,
    dataTermoDeUsoAceito: (form.dataTermoDeUsoAceito?.includes('/') ? form.dataTermoDeUsoAceito.split('/').reverse().join('-') : form.dataTermoDeUsoAceito) || null,
    telefone: form.telefone ? form.telefone.replace(/\D/g, '') : null,
    usuariosCompetencias: usuariosCompetencias
  }

  if (formData.foto)
    delete formData.foto

  busy.value = true

  if (avatarUploadRef.value?.hasStagedImage) {
    const result = await avatarUploadRef.value.submitUpload()

    if (!result) {
      busy.value = false
      return false
    }
  }

  return usuariosStore.update(formData, formData.id)
    .then((result) => {
      authStore.getUserInfo(formData.id)
      if (result?.id) emit('onComplete', result)

      return true
    })
    .catch((error) => {
      console.error(error)
    })
    .finally(() => {
      busy.value = false
    })
}

const handleFileUpload = async (data: string) => {
  try {
    const result = await usersService.uploadUserPicture({
      peopleId: form.peopleId,
      pictureBase64: data
    })

    if (result?.success) {
      authStore.setAvatar(data)

      toast.success({
        title: t('feedback.success'),
        message: t('feedback.profileImageSuccess')
      })
    }
  } catch (error) {
    console.error(error)
  }

  return true
}

const loadDataList = async () => {
  if (!competenciasStore.list.length) competenciasStore.getAll()
}

onMounted(async () => {
  birthday.value = false
  await loadDataList()

  originalCompetences.value = authStore.userLogged.usuariosCompetencias

  const assignData = {
    ...authStore.userLogged,
    dataDeAdmissao: authStore.userLogged.dataDeAdmissao ?? null,
    telefone: normalizePhone(authStore.userLogged.telefone),
    usuariosCompetencias: authStore.userLogged.usuariosCompetencias.map((el) => el.competenciaId)
  }

  Object.assign(form, assignData)

  loaded.value = true

  nextTick(() => {
    if (tabs[0]?.id == 1) selected.value[0].classList.add('indicator')

    if (!form?.dataDeNascimento) return

    const birthdate = new Date(form?.dataDeNascimento)
    const today = new Date()

    if (birthdate.getDate() === today.getDate() && birthdate.getMonth() === today.getMonth()) {
      birthday.value = true

      setTimeout(() => {
        const { reward: balloonsReward } = useReward('birthdateInput', 'balloons')

        const cake1 = useReward('birthdateInput', 'confetti', {
          zIndex: 2000,
          lifetime: 300,
          elementCount: 50,
          startVelocity: 30,
          spread: 60,
          angle: 140,
          elementSize: 6
        })

        const cake2 = useReward('birthdateInput', 'confetti', {
          zIndex: 2000,
          lifetime: 300,
          elementCount: 50,
          startVelocity: 30,
          spread: 20,
          angle: 60,
          elementSize: 6
        })

        const emoji = useReward('birthdateInput', 'emoji', {
          zIndex: 2000,
          lifetime: 100,
          elementCount: 4,
          emoji: ['🥳']
        })

        balloonsReward()
        cake1.reward()
        cake2.reward()
        emoji.reward()
      }, 2000)
    }
  })
})

defineExpose({
  submit,
  resetPassword,
  busy
})
</script>

<style lang="scss" scoped>
.indicator {
  border-bottom: 1px solid black;
  transition: all 0.2s ease-in-out;
}

.navlink {
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease-in-out;
  transform: translateY(0);

  &:hover,
  &.indicator {
    transform: translateY(-1px) scale(1.05);
  }
}
.navlink {
  border: none;
  position: relative;

  &::after {
    @apply rounded bg-neutral-300 dark:bg-neutral-700;
    content: '';
    position: absolute;
    width: 0px;
    height: 1px;
    left: 50%;
    bottom: 0;
    transition: all ease-in-out 0.2s;
  }

  &:hover::after {
    left: 25%;
    width: 50%;
  }

  &.indicator::after {
    @apply bg-current;
    width: 100%;
    left: 0;
  }
}
</style>
